/**
 * What is waiting at the dock — the receiving to-do, from the inbound board (the S&S order feed joined
 * with our ledger): delivered-but-unreceived shipments, part-received ones ("1 of 2 boxes received"),
 * and a short "on the way" list. Replaces the invoice-number chips: an invoice number means nothing
 * to the person holding the box; the PO, the blanks, the pieces and the box count do.
 */
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Truck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { cn } from '@/lib/utils';

type Shipment = components['schemas']['InventoryInboundShipment'];

/** Shared with the Inbound tab and the hub badge — a Receiving visit never re-hits S&S. */
export const INBOUND_KEY = ['inventory-inbound'] as const;

/** The blanks in a shipment, said once: "Tultex 602 Black + 2 more". */
function summary(s: Shipment): string {
  const names = [
    ...new Set(
      s.lines
        .map((l) => [l.brandName, l.styleName, l.colorName].filter(Boolean).join(' '))
        .filter(Boolean),
    ),
  ];
  if (names.length === 0) return `${s.lineCount} ${s.lineCount === 1 ? 'line' : 'lines'}`;
  return names.length === 1 ? (names[0] ?? '') : `${names[0]} + ${names.length - 1} more`;
}

/** S&S dates are `YYYY-MM-DDTHH:mm:ss` with no zone — read the day and show it as a day. */
function shortDate(iso: string | null | undefined): string | null {
  const day = iso?.slice(0, 10);
  if (!day) return null;
  const d = new Date(`${day}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function SectionTitle({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'amber' | 'blue';
}) {
  return (
    <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
      <span
        className={cn(
          'rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums normal-case tracking-normal',
          tone === 'amber'
            ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
            : 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
        )}
      >
        {count}
      </span>
    </h2>
  );
}

export function WaitingList({ onPick }: { onPick: (invoice: string) => void }) {
  const client = useApiClient();
  const inbound = useQuery({
    queryKey: INBOUND_KEY,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/inbound');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load inbound.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  const awaiting = (inbound.data?.awaitingReceipt ?? []).filter((s) => s.invoiceNumber);
  const transit = inbound.data?.shipments ?? [];
  if (awaiting.length === 0 && transit.length === 0) return null;
  const shownTransit = transit.slice(0, 4);

  return (
    <div className="space-y-5">
      {awaiting.length > 0 && (
        <section aria-label="Waiting to be received" className="space-y-2">
          <SectionTitle label="Waiting to be received" count={awaiting.length} tone="amber" />
          <ul className="space-y-2">
            {awaiting.map((s) => (
              <WaitingCard key={s.invoiceNumber ?? s.orderNumber} s={s} onPick={onPick} />
            ))}
          </ul>
        </section>
      )}
      {transit.length > 0 && (
        <section aria-label="On the way" className="space-y-2">
          <SectionTitle label="On the way" count={transit.length} tone="blue" />
          <ul className="divide-y rounded-lg border text-sm">
            {shownTransit.map((s) => {
              const eta = shortDate(s.expectedDeliveryDate);
              return (
                <li
                  key={s.invoiceNumber ?? s.orderNumber}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2"
                >
                  <span className="font-medium">
                    {s.poNumber ? s.poNumber : `Order ${s.orderNumber}`}
                  </span>
                  <span className="min-w-0 truncate text-muted-foreground">
                    {summary(s)} · {s.totalPieces} pieces
                    {s.totalBoxes
                      ? ` · ${s.totalBoxes} ${s.totalBoxes === 1 ? 'box' : 'boxes'}`
                      : ''}
                  </span>
                  <Badge
                    variant="outline"
                    className="ml-auto border-blue-300 text-blue-700 dark:border-blue-800 dark:text-blue-300"
                  >
                    <Truck className="size-3" /> In transit{eta ? ` · expected ${eta}` : ''}
                  </Badge>
                </li>
              );
            })}
            {transit.length > shownTransit.length && (
              <li className="px-3 py-2 text-xs text-muted-foreground">
                +{transit.length - shownTransit.length} more on the Inbound tab
              </li>
            )}
          </ul>
        </section>
      )}
    </div>
  );
}

function WaitingCard({ s, onPick }: { s: Shipment; onPick: (invoice: string) => void }) {
  const partly = s.boxesReceived != null;
  const expected = shortDate(s.expectedDeliveryDate);
  return (
    <li className={cn('rounded-lg border p-3', partly && 'border-amber-300')}>
      <div className="flex flex-wrap items-center gap-2">
        {s.poNumber ? (
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold">
            {s.poNumber}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            Website order <span className="font-mono">{s.orderNumber}</span>
          </span>
        )}
        <span className="font-medium">{summary(s)}</span>
        {partly ? (
          <Badge variant="outline" className="border-amber-300 text-amber-700 dark:text-amber-400">
            {s.boxesReceived} of {s.totalBoxes ?? '?'} boxes received
          </Badge>
        ) : s.deliveryState === 'partially_delivered' ? (
          <Badge variant="outline" className="border-amber-300 text-amber-700 dark:text-amber-400">
            <Truck className="size-3" /> Partly delivered
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="border-emerald-300 text-emerald-700 dark:text-emerald-400"
          >
            <CheckCircle2 className="size-3" /> Delivered
          </Badge>
        )}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">
        inv {s.invoiceNumber} · {s.totalPieces} {s.totalPieces === 1 ? 'piece' : 'pieces'} ·{' '}
        {s.lineCount} {s.lineCount === 1 ? 'line' : 'lines'}
        {s.totalBoxes ? ` · ${s.totalBoxes} ${s.totalBoxes === 1 ? 'box' : 'boxes'}` : ''}
        {s.shippingCarrier ? ` · ${s.shippingCarrier}` : ''}
        {expected ? ` · expected ${expected}` : ''}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button
          className="h-11 w-full sm:h-9 sm:w-auto"
          variant={partly ? 'outline' : 'default'}
          onClick={() => s.invoiceNumber && onPick(s.invoiceNumber)}
          aria-label={`${partly ? 'Open' : 'Receive'} invoice ${s.invoiceNumber}`}
        >
          {partly ? 'Open' : 'Receive'}
        </Button>
        {!partly && (s.totalBoxes ?? 0) > 1 && (
          <span className="text-xs text-muted-foreground">
            or scan one carton to receive just that box
          </span>
        )}
      </div>
    </li>
  );
}
